/**
 * Stubs browser permission/geolocation APIs used by HERE/Baidu map SDKs.
 * Headless Chrome rejects these async checks; unresolved rejections surface in afterAll.
 * All mutating helpers are no-ops outside Karma (polyfills.ts is bundled for production).
 */
export function isKarmaTestEnvironment(): boolean {
  return (
    typeof (globalThis as { __karma__?: unknown }).__karma__ !== 'undefined'
  );
}

/** Early bootstrap for polyfills.ts — only runs under Karma. */
export function installMapSdkTestStubsIfKarma(): void {
  if (!isKarmaTestEnvironment()) {
    return;
  }
  patchUnhandledRejectionListenersForMapSdks();
  stubNavigatorForMapSdks();
  installBenignRejectionSuppression();
}

export function isBenignMapSdkRejection(reason: unknown): boolean {
  let message = '';
  if (reason instanceof Error) {
    message = reason.message;
  } else if (typeof reason === 'string') {
    message = reason;
  } else if (reason && typeof reason === 'object' && 'message' in reason) {
    message = String((reason as { message?: unknown }).message ?? '');
  } else if (reason != null) {
    message = String(reason);
  }
  return (
    message.includes('Permissions check failed') ||
    message.includes('Only secure origins are allowed') ||
    message.includes('Geolocation') ||
    message.includes('geolocation')
  );
}

export function stubNavigatorForMapSdks(): void {
  if (!isKarmaTestEnvironment() || typeof navigator === 'undefined') {
    return;
  }

  const grantedPermission = {
    state: 'granted',
    onchange: null,
  } as PermissionStatus;

  const permissionsApi = {
    query: (_descriptor: PermissionDescriptor) =>
      Promise.resolve(grantedPermission),
  };

  const geolocationApi = {
    getCurrentPosition: (
      success: PositionCallback,
      _error?: PositionErrorCallback
    ) =>
      success({
        coords: {
          latitude: 0,
          longitude: 0,
          accuracy: 0,
          altitude: null,
          altitudeAccuracy: null,
          heading: null,
          speed: null,
        },
        timestamp: Date.now(),
      } as GeolocationPosition),
    watchPosition: () => 0,
    clearWatch: () => undefined,
  };

  if (typeof Navigator !== 'undefined' && Navigator.prototype) {
    try {
      Object.defineProperty(Navigator.prototype, 'permissions', {
        configurable: true,
        get: () => permissionsApi,
      });
    } catch {
      // ignore read-only prototype in some runtimes
    }
  }

  try {
    Object.defineProperty(navigator, 'permissions', {
      configurable: true,
      writable: true,
      value: permissionsApi,
    });
  } catch {
    (navigator as any).permissions = permissionsApi;
  }

  try {
    Object.defineProperty(navigator, 'geolocation', {
      configurable: true,
      writable: true,
      value: geolocationApi,
    });
  } catch {
    (navigator as any).geolocation = geolocationApi;
  }
}

/** Drain microtasks and one macrotask so map SDK permission promises settle before teardown. */
export async function drainPendingAsyncWork(): Promise<void> {
  await Promise.resolve();
  await Promise.resolve();
  await new Promise<void>(resolve => setTimeout(resolve, 0));
}

function suppressUnhandledRejection(event: PromiseRejectionEvent): void {
  if (!isBenignMapSdkRejection(event.reason)) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
}

let unhandledRejectionListenerInstalled = false;
let addEventListenerPatched = false;

function wrapUnhandledRejectionListener(
  listener: EventListenerOrEventListenerObject
): EventListener {
  const invoke = (event: Event) => {
    if (typeof listener === 'function') {
      listener.call(globalThis, event);
      return;
    }
    listener.handleEvent(event);
  };

  return (event: Event) => {
    const rejectionEvent = event as PromiseRejectionEvent;
    if (isBenignMapSdkRejection(rejectionEvent.reason)) {
      rejectionEvent.preventDefault();
      rejectionEvent.stopImmediatePropagation();
      return;
    }
    invoke(event);
  };
}

/**
 * Jasmine 3.x registers bubble-phase unhandledrejection handlers before polyfills load.
 * Wrap future handlers so map SDK permission rejections never reach Jasmine.
 */
export function patchUnhandledRejectionListenersForMapSdks(): void {
  if (
    !isKarmaTestEnvironment() ||
    addEventListenerPatched ||
    typeof EventTarget === 'undefined'
  ) {
    return;
  }

  addEventListenerPatched = true;
  const originalAddEventListener = EventTarget.prototype.addEventListener;

  EventTarget.prototype.addEventListener = function (
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions
  ): void {
    if (type === 'unhandledrejection' && listener) {
      originalAddEventListener.call(
        this,
        type,
        wrapUnhandledRejectionListener(listener),
        options
      );
      return;
    }

    originalAddEventListener.call(
      this,
      type,
      listener as EventListener,
      options
    );
  };
}

/** Jasmine 3.x reports browser rejections via global onerror, not env.onUnhandledError. */
export function patchGlobalOnErrorForMapSdks(): void {
  if (!isKarmaTestEnvironment()) {
    return;
  }

  const currentUnhandled = (
    globalThis as {
      onunhandledrejection?: ((event: PromiseRejectionEvent) => unknown) | null;
    }
  ).onunhandledrejection;
  if (
    !(currentUnhandled as { __mapSdkPatched?: boolean } | null | undefined)
      ?.__mapSdkPatched
  ) {
    const previousUnhandled = currentUnhandled;
    const wrappedUnhandled = (event: PromiseRejectionEvent): unknown => {
      if (isBenignMapSdkRejection(event.reason)) {
        event.preventDefault();
        return true;
      }
      if (typeof previousUnhandled === 'function') {
        return previousUnhandled.call(globalThis, event);
      }
      return false;
    };
    (wrappedUnhandled as { __mapSdkPatched?: boolean }).__mapSdkPatched = true;
    (
      globalThis as {
        onunhandledrejection?:
          | ((event: PromiseRejectionEvent) => unknown)
          | null;
      }
    ).onunhandledrejection = wrappedUnhandled;
  }

  const current = globalThis.onerror as
    | ((...args: unknown[]) => unknown)
    | null
    | undefined;
  if (current && (current as { __mapSdkPatched?: boolean }).__mapSdkPatched) {
    return;
  }

  const previous = current;

  const wrapped = (
    message: Event | string,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error
  ): boolean => {
    if (
      isBenignMapSdkRejection(error) ||
      isBenignMapSdkRejection(message) ||
      (typeof message === 'string' &&
        message.indexOf('Unhandled promise rejection') !== -1 &&
        isBenignMapSdkRejection(message))
    ) {
      return true;
    }
    if (typeof previous === 'function') {
      return previous.call(
        globalThis,
        message,
        source,
        lineno,
        colno,
        error
      ) as boolean;
    }
    return false;
  };

  (wrapped as { __mapSdkPatched?: boolean }).__mapSdkPatched = true;
  globalThis.onerror = wrapped;
}

export function installBenignRejectionSuppression(): void {
  if (!isKarmaTestEnvironment()) {
    return;
  }

  patchUnhandledRejectionListenersForMapSdks();

  if (!unhandledRejectionListenerInstalled) {
    unhandledRejectionListenerInstalled = true;
    globalThis.addEventListener(
      'unhandledrejection',
      suppressUnhandledRejection,
      true
    );
  }

  patchGlobalOnErrorForMapSdks();
}
